/**
 * AnalysisLayout — shared chrome for the per-scan area of vuln-mgmt.
 *
 * Owns the PageLayout + scan sub-header (name + version switcher +
 * severity circles) + Components/Vulnerabilities tab strip. Renders
 * an <Outlet /> for the body so the chrome stays mounted across the
 * two child routes:
 *
 *   /analysis/:uuid                       → index, renders <Report />
 *   /analysis/:uuid/vulnerability/:vulnId → renders <VulnerabilityDetail />
 *
 * Net effect: clicking a vuln row on the table transitions to the
 * detail body **without re-mounting the sub-header** — same chrome,
 * different body. The previous flat-routes layout had both pages
 * mount their own PageLayout, so the sub-header flashed on every
 * transition.
 *
 * Children read `analysisData` / `isLatest` / `versions` via
 * `useOutletContext()` from react-router-dom so they don't have to
 * re-fetch the scan details or duplicate the navigation-state read.
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams, Outlet } from 'react-router-dom';
import { PageLayout } from '../../components/layout';
import SubHeaderWithTabs from '../../components/common/SubHeaderWithTabs';
import SwitcherPill from '../../components/common/SwitcherPill';
import CategoryPill from '../../components/ui/CategoryPill';
import VulnerabilityManagementService from '../../services/VulnerabilityManagementService';
import { getSeverityColor } from '../../utils/severityStyle';
import reportStyles from './Report.module.css';

const VALID_TABS = new Set(['components', 'vulnerabilities']);
const DEFAULT_TAB = 'components';

const baseBreadcrumbs = [
  { label: 'Dashboard', path: '/athena' },
  { label: 'Vulnerability Management', path: '/athena/vulnerability-management' },
];

export default function AnalysisLayout() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [analysisData, setAnalysisData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [versionOpen, setVersionOpen] = useState(false);

  // Cache nav-state values on first mount so they survive within-
  // layout child-route swaps. `location.state` is per-navigation —
  // once the user transitions from index → detail, the dashboard's
  // original state is no longer the active location.state. Caching
  // on mount keeps `isLatest` + `versions` durable for the layout's
  // lifetime. (Cross-refresh durability is a separate Phase 8 item:
  // add `isLatest` to the backend scan-details response.)
  const [shellState] = useState(() => ({
    isLatest: location.state?.isLatest ?? false,
    versions: location.state?.versions ?? [],
  }));
  const { isLatest, versions } = shellState;

  // Detect child detail route (.../vulnerability/:vulnId). When
  // present, the body is the per-vuln page; sub-header pins the
  // Vulnerabilities tab as active (that's where the user came from).
  const onDetailRoute = location.pathname.includes('/vulnerability/');

  const rawTabParam = searchParams.get('tab');
  const indexActiveTab = rawTabParam && VALID_TABS.has(rawTabParam) ? rawTabParam : DEFAULT_TAB;
  const activeTab = onDetailRoute ? 'vulnerabilities' : indexActiveTab;

  const setActiveTab = (next) => {
    if (onDetailRoute) {
      // Tab click from the detail page returns to the index route
      // with the selected tab active.
      const search = next === DEFAULT_TAB ? '' : `?tab=${next}`;
      navigate(`/athena/vulnerability-management/analysis/${uuid}${search}`);
      return;
    }
    setSearchParams(
      (prev) => {
        const updated = new URLSearchParams(prev);
        if (next === DEFAULT_TAB) updated.delete('tab');
        else updated.set('tab', next);
        return updated;
      },
      { replace: true },
    );
  };

  useEffect(() => {
    if (!uuid) return;
    let cancelled = false;
    const fetchDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        const details = await VulnerabilityManagementService.getScanDetails(uuid);
        if (!cancelled) setAnalysisData(details);
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching analysis details:', err);
        setError(err.message || 'Failed to load analysis details');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchDetails();
    return () => {
      cancelled = true;
    };
  }, [uuid]);

  // Breadcrumb derivation:
  //   index route  → [Dashboard, Vuln Mgmt],            currentPage = scan name
  //   detail route → [Dashboard, Vuln Mgmt, scan name],  currentPage = vulnId
  const detailVulnId = onDetailRoute ? location.pathname.split('/').pop() : null;
  const currentPage = onDetailRoute ? detailVulnId : (analysisData?.name || 'Analysis Report');
  const breadcrumbItems = onDetailRoute
    ? [
        ...baseBreadcrumbs,
        {
          label: analysisData?.name || 'Analysis',
          path: `/athena/vulnerability-management/analysis/${uuid}`,
        },
      ]
    : baseBreadcrumbs;

  if (loading) {
    return (
      <PageLayout
        activeNavItem="vulnerability-management"
        currentPage="Analysis Report"
        breadcrumbItems={baseBreadcrumbs}
      >
        <div className={reportStyles.statusMessage}>Loading analysis data...</div>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout
        activeNavItem="vulnerability-management"
        currentPage="Analysis Report"
        breadcrumbItems={baseBreadcrumbs}
      >
        <div className={reportStyles.statusError}>Error: {error}</div>
      </PageLayout>
    );
  }

  const SEVERITY_CIRCLES = [
    { label: 'Critical',   value: analysisData?.critical || 0, color: getSeverityColor('Critical') },
    { label: 'High',       value: analysisData?.high     || 0, color: getSeverityColor('High')     },
    { label: 'Medium',     value: analysisData?.medium   || 0, color: getSeverityColor('Medium')   },
    { label: 'Low',        value: analysisData?.low      || 0, color: getSeverityColor('Low')      },
    { label: 'Unassigned', value: 0,                           color: '#6B7280'                    },
  ];

  const headerContent = (
    <div className={reportStyles.scanHeader}>
      <div className={reportStyles.scanInfo}>
        <div className={reportStyles.scanMeta}>
          <div className={reportStyles.scanNameGroup}>
            <span className={reportStyles.scanName}>{analysisData?.name || 'Analysis Report'}</span>
            {versions.length > 1 ? (
              <SwitcherPill
                label={analysisData?.version || 'version'}
                isOpen={versionOpen}
                onToggle={() => setVersionOpen((v) => !v)}
                onClose={() => setVersionOpen(false)}
                triggerTitle="Switch version"
                dropdownClassName={reportStyles.versionDropdown}
              >
                {versions.map((v) => (
                  <button
                    key={v.uuid}
                    type="button"
                    role="menuitem"
                    className={`${reportStyles.versionDropdownItem} ${v.uuid === uuid ? reportStyles.versionDropdownItemActive : ''}`}
                    onClick={() => {
                      setVersionOpen(false);
                      navigate(`/athena/vulnerability-management/analysis/${v.uuid}`, {
                        state: { isLatest: v.isLatest, versions },
                      });
                    }}
                  >
                    <span>v{v.version}</span>
                    {v.isLatest && <CategoryPill label="LATEST" size="sm" />}
                  </button>
                ))}
              </SwitcherPill>
            ) : (
              analysisData?.version && (
                <span className={reportStyles.versionText}>{analysisData.version}</span>
              )
            )}
            {isLatest && <CategoryPill label="LATEST" size="sm" />}
          </div>
        </div>
      </div>
      <div className={reportStyles.severityCircles}>
        {SEVERITY_CIRCLES.map(({ label, value, color }) => (
          <div
            key={label}
            className={reportStyles.severityCircle}
            style={{ borderColor: color, color }}
            title={`${value} ${label} Severity Vulnerabilities`}
          >
            {value}
          </div>
        ))}
      </div>
    </div>
  );

  const tabsConfig = [
    { id: 'components', label: 'Components' },
    { id: 'vulnerabilities', label: 'Vulnerabilities' },
  ];

  return (
    <PageLayout
      activeNavItem="vulnerability-management"
      currentPage={currentPage}
      breadcrumbItems={breadcrumbItems}
      contentClassName={reportStyles.contentAreaFullWidth}
      disableScroll
    >
      <div className={reportStyles.reportContainer}>
        <SubHeaderWithTabs
          header={headerContent}
          tabs={tabsConfig}
          activeTabId={activeTab}
          onTabChange={setActiveTab}
          tabsWrapClassName={reportStyles.tabBar}
          tabsStripClassName={reportStyles.tabStrip}
          tabClassName={reportStyles.tab}
          activeTabClassName={reportStyles.tabActive}
        />
        <Outlet context={{ analysisData, isLatest, versions }} />
      </div>
    </PageLayout>
  );
}
